import { CSSResultGroup, html, LitElement, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { style } from './range-slider.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'

@customElement('cse-range-slider')
export class CSERangeSlider extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Number })
  min = 0

  @property({ type: Number })
  max = 100

  @property({ type: Number, reflect: true, attribute: 'start-value' })
  startValue = 0

  @property({ type: Number, reflect: true, attribute: 'stop-value' })
  stopValue = 100

  @property({ type: Number })
  step = 1

  @property({ type: Boolean })
  disabled = false

  @state()
  _start: number = 0

  @state()
  _stop: number = 100

  private calculate(value: number) {
    const all = Math.abs(Number(this.min)) + Number(this.max)
    return 100 / all * (value - this.min)
  }

  private handleChange(event: Event) {
    event.stopPropagation()
    const target = event.target as HTMLInputElement

    if (target.name === 'start') {
      this._start = this.calculate(parseFloat(target.value))
      this.startValue = Number(target.value)

      if (this.startValue > this.stopValue) {
        this.stopValue = this.startValue + this.step
        this._stop = this.calculate(this.stopValue)
      }
    } else if (target.name === 'stop') {
      this._stop = this.calculate(parseFloat(target.value))
      this.stopValue = Number(target.value)

      if (this.stopValue < this.startValue) {
        this.startValue = this.stopValue - this.step
        this._start = this.calculate(this.startValue)
      }
    }

    // Dispatch change event on the custom element
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }))
  }

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties)
    this._start = this.calculate(this.startValue)
    this._stop = this.calculate(this.stopValue)
  }

  private renderInputRange = (name: string, value: number) => html`
    <label class="sr-only" for="slider-${name}">${name}</label>
    <input
      id="slider-${name}"
      type="range"
      name="${name}"
      min="${this.min}"
      max="${this.max}"
      .value="${value}"
      step="${this.step}"
      ?disabled="${this.disabled}"
      @change="${this.handleChange}"
    />
  `

  override render() {
    return html`
      <div
        class="cse-range-slider"
        style="--start: ${this._start}%; --stop: ${this._stop}%"
      >
        ${this.renderInputRange('start', this.startValue)}
        ${this.renderInputRange('stop', this.stopValue)}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-range-slider': CSERangeSlider
  }
}
